// Dépôt d'un signalement (D-042) : catégorie, description, lieu (carte, appareil ou coordonnées),
// adresse facultative, jusqu'à cinq photos (JPEG ou PNG) envoyées une par une après le dépôt ;
// confirmation avec le numéro SIG et lien vers le suivi.
import { CATEGORIES_SIGNALEMENT, type CategorieSignalement } from '@cc/contrats';
import { Bandeau, Bouton, Champ, FichierChamp, Formulaire, LienBouton, Selection, ZoneTexte } from '@cc/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { messageErreur } from '../../../api/erreurs';
import { useCollectiviteChoisie } from '../collectivite';
import { Bloc, EnTeteCitoyen, ExigeConnexion } from '../commun';
import { ajouterPhoto, CLES_SIGNALEMENTS, deposerSignalement, type SignalementUsager } from './api';
import { SaisiePosition, type Position } from './SaisiePosition';
import styles from '../citoyen.module.css';

export const MAX_PHOTOS = 5;
const DIX_MO = 10 * 1024 * 1024;

function Depot({ code }: { code: string }) {
  const client = useQueryClient();
  const [categorie, setCategorie] = useState<CategorieSignalement | ''>('');
  const [description, setDescription] = useState('');
  const [adresse, setAdresse] = useState('');
  const [position, setPosition] = useState<Position | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [erreurs, setErreurs] = useState<{ champ: string; message: string }[]>([]);
  const [envoi, setEnvoi] = useState<{ enCours: boolean; etape: string | null; erreur: string | null }>({ enCours: false, etape: null, erreur: null });
  const [depose, setDepose] = useState<{ s: SignalementUsager; photosEchouees: number } | null>(null);
  const erreur = (c: string) => erreurs.find((e) => e.champ === c)?.message ?? null;

  const soumettre = async () => {
    const l = [
      !categorie ? { champ: 'sig-categorie', message: 'Choisissez une catégorie' } : null,
      description.trim().length < 3 ? { champ: 'sig-description', message: 'Décrivez le problème' } : null,
      !position ? { champ: 'sig-latitude', message: 'Placez le lieu sur la carte, utilisez la position de l\'appareil ou saisissez les coordonnées' } : null,
      photos.length > MAX_PHOTOS ? { champ: 'sig-photos', message: `Au plus ${MAX_PHOTOS} photos` } : null,
    ].filter((x): x is { champ: string; message: string } => x !== null);
    setErreurs(l);
    if (l.length > 0 || !position || !categorie) return;
    setEnvoi({ enCours: true, etape: 'Envoi du signalement', erreur: null });
    try {
      let s = await deposerSignalement({ collectivite: code, categorie, description: description.trim(), position, adresse: adresse.trim() || undefined });
      let echouees = 0;
      for (const [i, f] of photos.entries()) {
        setEnvoi({ enCours: true, etape: `Envoi de la photo ${i + 1} sur ${photos.length}`, erreur: null });
        try {
          s = await ajouterPhoto(s.id, f);
        } catch {
          echouees += 1;
        }
      }
      await client.invalidateQueries({ queryKey: CLES_SIGNALEMENTS.liste });
      setDepose({ s, photosEchouees: echouees });
    } catch (e) {
      setEnvoi({ enCours: false, etape: null, erreur: messageErreur(e) });
    }
  };

  if (depose) {
    return (
      <div className={styles.pile}>
        <Bloc etiquette="Signalement déposé" titre={depose.s.categorie_libelle}>
          <span className={`${styles.mono} ${styles.blocTitre}`}>{depose.s.numero}</span>
          <p className={styles.blocTexte}>
            {depose.s.collectivite.nom} va qualifier votre signalement. Vous serez informé de chaque étape : prise en compte, planification de l&apos;intervention, clôture.
            {depose.s.photos.length > 0 ? ` ${depose.s.photos.length} photo${depose.s.photos.length > 1 ? 's' : ''} jointe${depose.s.photos.length > 1 ? 's' : ''}.` : ''}
          </p>
          {depose.photosEchouees > 0 ? <Bandeau type="attention">{depose.photosEchouees} photo{depose.photosEchouees > 1 ? 's n\'ont' : ' n\'a'} pas pu être envoyée{depose.photosEchouees > 1 ? 's' : ''} : vous pouvez les ajouter depuis le suivi.</Bandeau> : null}
        </Bloc>
        <div className={styles.ligneBoutons}>
          <LienBouton href={`/citoyen/signalements/${depose.s.id}`} variante="principal">
            Suivre ce signalement
          </LienBouton>
          <LienBouton href="/citoyen/signalements" variante="secondaire">
            Mes signalements
          </LienBouton>
        </div>
      </div>
    );
  }
  return (
    <Formulaire
      libelle="Nouveau signalement"
      onSoumettre={() => void soumettre()}
      erreurs={erreurs}
      actions={
        <Bouton type="submit" chargement={envoi.enCours} libelleChargement={envoi.etape ?? 'Envoi en cours'} pleineLargeur>
          Envoyer le signalement
        </Bouton>
      }
    >
      {envoi.erreur ? <Bandeau type="erreur">{envoi.erreur}</Bandeau> : null}
      <Selection
        id="sig-categorie"
        libelle="Catégorie"
        optionVide="Choisir"
        options={Object.entries(CATEGORIES_SIGNALEMENT).map(([valeur, libelle]) => ({ valeur, libelle }))}
        value={categorie}
        onChange={(e) => setCategorie(e.target.value as CategorieSignalement | '')}
        erreur={erreur('sig-categorie')}
        required
      />
      <ZoneTexte id="sig-description" libelle="Ce que vous constatez" value={description} onChange={(e) => setDescription(e.target.value)} erreur={erreur('sig-description')} required rows={4} maxLength={5000} aide="Par exemple : lampadaire éteint depuis trois nuits devant l'école" />
      <SaisiePosition code={code} position={position} onPosition={setPosition} erreur={erreur('sig-latitude')} />
      <Champ id="sig-adresse" libelle="Adresse ou repère" value={adresse} onChange={(e) => setAdresse(e.target.value)} facultatif maxLength={300} />
      <FichierChamp id="sig-photos" libelle={`Photos (jusqu'à ${MAX_PHOTOS}, JPEG ou PNG)`} fichiers={photos} onFichiers={setPhotos} accept=".jpg,.jpeg,.png" tailleMax={DIX_MO} facultatif erreur={erreur('sig-photos')} />
    </Formulaire>
  );
}

export function PageNouveauSignalement() {
  const [choisie] = useCollectiviteChoisie();
  if (!choisie) return <Navigate to="/citoyen" replace />;
  return (
    <>
      <EnTeteCitoyen titre="Signaler un problème" retour="/citoyen/signalements" libelleRetour="Signalements" sousTitre={choisie.nom} />
      <ExigeConnexion titre="Connectez-vous pour signaler un problème">
        <Depot code={choisie.code} />
      </ExigeConnexion>
    </>
  );
}
